/**
 * Persistent progress and settings in localStorage. Every access is guarded: private browsing,
 * disabled storage or corrupt data fall back to defaults instead of breaking the game.
 */

export interface LevelRecord {
  /** Best stars earned (1 to 3). */
  stars: number;
  /** Lightest winning weight. */
  best: number;
}

export interface Progress {
  levels: Record<string, LevelRecord>;
  endless: { solved: number; streak: number };
  /** Level 1 onboarding gesture has been shown and used. */
  onboarded: boolean;
}

export interface Settings {
  sound: boolean;
  volume: number;
  /** 'system' follows prefers-reduced-motion. */
  motion: 'system' | 'reduce' | 'full';
  patterns: boolean;
  contrast: boolean;
  haptics: boolean;
}

const PROGRESS_KEY = 'lensing:v1:progress';
const SETTINGS_KEY = 'lensing:v1:settings';

export const DEFAULT_SETTINGS: Settings = {
  sound: true,
  volume: 0.7,
  motion: 'system',
  patterns: false,
  contrast: false,
  haptics: true,
};

function emptyProgress(): Progress {
  return { levels: {}, endless: { solved: 0, streak: 0 }, onboarded: false };
}

function read(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): boolean {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/** Parse stored progress defensively (unknown shapes are dropped, not trusted). */
export function parseProgress(data: unknown): Progress {
  const p = emptyProgress();
  if (!isObj(data)) return p;
  if (isObj(data.levels)) {
    for (const [id, rec] of Object.entries(data.levels)) {
      if (!isObj(rec)) continue;
      const stars = Number(rec.stars);
      const best = Number(rec.best);
      if (
        Number.isInteger(stars) &&
        stars >= 1 &&
        stars <= 3 &&
        Number.isFinite(best) &&
        best >= 0
      ) {
        p.levels[id] = { stars, best };
      }
    }
  }
  if (isObj(data.endless)) {
    const solved = Number(data.endless.solved);
    const streak = Number(data.endless.streak);
    if (Number.isInteger(solved) && solved >= 0) p.endless.solved = solved;
    if (Number.isInteger(streak) && streak >= 0) p.endless.streak = streak;
  }
  p.onboarded = data.onboarded === true;
  return p;
}

export function parseSettings(data: unknown): Settings {
  const s = { ...DEFAULT_SETTINGS };
  if (!isObj(data)) return s;
  if (typeof data.sound === 'boolean') s.sound = data.sound;
  if (typeof data.volume === 'number' && data.volume >= 0 && data.volume <= 1)
    s.volume = data.volume;
  if (data.motion === 'system' || data.motion === 'reduce' || data.motion === 'full')
    s.motion = data.motion;
  if (typeof data.patterns === 'boolean') s.patterns = data.patterns;
  if (typeof data.contrast === 'boolean') s.contrast = data.contrast;
  if (typeof data.haptics === 'boolean') s.haptics = data.haptics;
  return s;
}

type Listener = () => void;

class Store {
  progress: Progress = parseProgress(read(PROGRESS_KEY));
  settings: Settings = parseSettings(read(SETTINGS_KEY));
  private listeners = new Set<Listener>();

  onChange(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }

  updateSettings(patch: Partial<Settings>) {
    this.settings = { ...this.settings, ...patch };
    write(SETTINGS_KEY, this.settings);
    this.emit();
  }

  /** Record a win; keeps the best stars and lightest weight. Returns true if it improved. */
  recordWin(id: string, stars: number, weight: number): boolean {
    const old = this.progress.levels[id];
    const rec: LevelRecord = {
      stars: Math.max(old?.stars ?? 0, stars),
      best: Math.min(old?.best ?? Infinity, weight),
    };
    const improved = !old || rec.stars > old.stars || rec.best < old.best;
    this.progress.levels[id] = rec;
    write(PROGRESS_KEY, this.progress);
    this.emit();
    return improved;
  }

  recordEndless(won: boolean) {
    if (won) {
      this.progress.endless.solved++;
      this.progress.endless.streak++;
    } else {
      this.progress.endless.streak = 0;
    }
    write(PROGRESS_KEY, this.progress);
    this.emit();
  }

  setOnboarded() {
    if (this.progress.onboarded) return;
    this.progress.onboarded = true;
    write(PROGRESS_KEY, this.progress);
  }

  resetProgress() {
    this.progress = emptyProgress();
    write(PROGRESS_KEY, this.progress);
    this.emit();
  }

  /** Whether motion should be reduced right now (setting or system preference). */
  get reducedMotion(): boolean {
    if (this.settings.motion === 'reduce') return true;
    if (this.settings.motion === 'full') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }
}

export const store = new Store();
